<template>
  <div class="-playable-state">
    <div ref="rcp" class="wt-rtp-player">
      <canvas for="player"></canvas>
      <canvas for="movie-processor"></canvas>
      <canvas for="diagnostic"></canvas>
      <div for="buffer-indicator">
        <img
          src="https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/instant-player/preloader.svg"
        />
      </div>
      <img
        for="pause-indicator"
        :alt="'Play'"
        src="/resources/img/play-icon-02.svg"
      />
      <img
        for="play-indicator"
        :alt="'Pause'"
        src="/resources/img/pause-icon-02.svg"
      />
      <div ref="rcpPanel"></div>
    </div>
  </div>
</template>

<script>
import RCP from '@wochitnpm/rcp';
import RcpControlPanel from 'v/assetModal/RcpControlPanel.vue';

export default {
  data() {
    return {
      parts: [],
      state: {
        /** FINAL' || 'PARTIAL'*/
        mode: null,
        /** parts currently present in rcp */
        parts: [],
      },
    };
  },

  props: {
    previewState: {
      type: Object,
      required: true,
    },
    storyboard: {
      type: Object,
      required: true,
    },
  },

  mounted() {
    this.rcp = new RCP({
      showDiagnostic: false,
      el: this.$refs.rcp,
      width: 656,
      height: 369,
      innerAspectRatio: app.ASPECT_RATIOS[this.storyboard.aspectRatio].ratio,
    });

    this.rcp.on(RCP.MSG.VOLUMECHANGE, this.reflectVolumeChange.bind(this));

    this.rcpPanel = new RcpControlPanel({
      el: this.$refs.rcpPanel,
      rcp: this.rcp,
    });

    if (this.previewState) {
      if (this.previewState.status === 'FINISHED') {
        this.setVideoUrl(this.previewState.videoUrl);
      } else {
        this.parts = this.previewState.parts;
        this.marshalPreviewParts();
      }
    }

    app.keyboardAccessibility.add(app.KEYBOARD_LAYER.MPP, [
      { on: 'keydown', key: $.ui.keyCode.SPACE, run: this.togglePlay },
      { on: 'keydown', key: $.ui.keyCode.LEFT, run: this.seek },
      { on: 'keydown', key: $.ui.keyCode.RIGHT, run: this.seek },
    ]);
  },

  beforeDestroy: function () {
    app.keyboardAccessibility.remove(app.KEYBOARD_LAYER.MPP);
    this.rcpPanel && this.rcpPanel.$destroy();
    this.rcp && this.rcp.destroy();
  },

  methods: {
    togglePlay: function (e) {
      if (!this.$isEditable(e.target)) {
        e.preventDefault();
        this.rcp.togglePlay();
      }
    },

    seek: function (e) {
      if (!this.$isEditable(e.target)) {
        e.preventDefault();
        const inclination = e.which === $.ui.keyCode.LEFT ? -1 : 1;
        const ct = this.rcp.getCurrentTime();
        const seekDuration = this.rcp.isPlaying() ? 5 : 0.1 / 3;
        const seekOffset = seekDuration * inclination;
        this.rcp.seek(ct + seekOffset);
      }
    },

    setVideoUrl: function (url, play = true) {
      this.state.mode = 'FINAL';
      this.rcp.reset();

      const videoDuration = this.previewState.duration;
      this._addVideo(url, videoDuration, play);

      this.rcpPanel.time.total = videoDuration;
      this.rcpPanel.time.buffered = this.rcpPanel.time.total;
    },

    _addVideo: function (src, duration, play) {
      this.rcp.append({
        play: play,
        item: {
          type: 'video',
          volume: 1,
          src: app.prependHttp(src),
          play: { duration: duration },
        },
      });
    },

    reflectVolumeChange(volume) {
      if (
        this.rcp.playList.hasNoVisualContent() ||
        _.isEmpty(this.rcp.playList.feed.bgMusic)
      ) {
        return;
      }

      const tItem = this.rcp.playList.feed.bgMusic[0];
      const mItem = tItem && tItem.getMediaItem();
      if (mItem) {
        mItem.el.volume = volume;
      }
    },
    marshalPreviewParts() {
      this.state.mode = 'PARTIAL';
      const state = {
        jobParts: this.parts,
        parts: [],
        isFirst: false,
        hasLast: false,
        newParts: [],
        autoplay: false,
      };
      // normalize order of the `state.jobParts`
      // i.e: allow following state.parts := [undefined, {}, undefined, {}]
      _.each(
        state.jobParts,
        function (part, key) {
          /*
       (key) 'AUDIO': {
       data: {cdnUrl},
       message: 'DONE'             // or NA
       }
       */
          if ('AUDIO' === key) {
            if (!this._audioPart) {
              this._audioPart = {
                available: part.message === 'DONE',
                cdnUrl: part.data && part.data.cdnUrl,
              };
              if (this._audioPart.available) {
                this.rcp.append({
                  play: true,
                  item: {
                    type: 'bgMusic',
                    src: app.prependHttp(this._audioPart.cdnUrl),
                    volume: 1,
                    blob: false,
                  },
                });
                if (!this.rcp.playList.hasNoVisualContent()) {
                  this.rcp.play();
                }
              }
              // console.info(`FPPC::AUDIO @`, jobCarrier.time.passed().toFixed(1), 'sec');
            }
            return true; // continue;
          }

          /* single part
       (key) 0002: (part) {
       data: {cdnUrl, duration}   // actual data
       message:"DONE"             // or COMPLETED
       timestamp:1481817902       // unix timestamp
       }
       */
          const iPartNumber = parseInt(key, 10);
          if ('0000' === key && _.isEmpty(this.state.parts)) {
            state.isFirst = true;
          }
          part.data.number = iPartNumber;
          if ('COMPLETED' === part.message) {
            state.hasLast = true;
            part.data.isLast = true;
          }
          state.parts[iPartNumber] = part.data;
        }.bind(this)
      );

      // add only if this.state.get('parts').length exists in state.parts
      for (
        let i = this.state.parts.length, I = state.parts.length;
        i < I;
        i++
      ) {
        if (state.parts[i]) {
          state.newParts.push(state.parts[i]);
        } else {
          break;
        }
      }

      if (state.newParts.length) {
        state.autoplay = !!this._audioPart;
        _.each(
          state.newParts,
          function (part) {
            if (part.isLast && part.duration < 0.1) {
              console.info('FPPC::Last part ignored');
            } else {
              this.rcp.append({
                play: state.autoplay,
                item: {
                  type: 'video',
                  src: app.prependHttp(part.cdnUrl),
                  blob: false,
                  play: { duration: part.duration },
                  volume: 0.0,
                  origin: `Part #${part.number}`,
                },
              });
            }
            this.state.parts.push(part);
          }.bind(this)
        );
      }

      if (state.isFirst) {
        this.rcpPanel.time.total = this.previewState.duration;
      } else if (state.hasLast) {
        this.rcpPanel.time.total = this.rcp.playList.feed.totalDisplayTime;
      }

      this.rcpPanel.time.buffered = _.sumBy(this.state.parts, function (part) {
        return part.duration;
      });
    },
  },
  watch: {
    previewState: {
      handler() {
        this.parts = this.previewState.parts;
        this.marshalPreviewParts();
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
.-playable-state {
  .wt-rtp-player {
    width: 656px;
    height: 369px;
  }
}
</style>
